import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../services/api';

export interface EmployerGig {
  id: number;
  employer_id: number;
  external_id: string;
  title: string;
  description: string;
  company: string;
  location: string;
  rate: string;
  rate_min: number | null;
  rate_max: number | null;
  rate_type: string;
  job_type: string;
  roles_needed: string[];
  skills_required: string[];
  certifications_required: string[];
  union_required: string;
  start_date: string | null;
  end_date: string | null;
  crew_size: number;
  is_urgent: boolean;
  is_active: boolean;
  expires_at?: string | null;
  is_publicly_qualified: boolean;
  qualification_reason: string | null;
  needs_attention: boolean;
  posting_tier?: string | null;
  payment_status?: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  match_count?: number;
  applied_count?: number;
  sms_booking_available?: boolean;
  created_at: string;
}

export interface MatchedWorker {
  id: number;
  worker_id: number;
  match_score: number;
  match_reasons: string[];
  status: string;
  first_name: string;
  last_initial: string | null;
  last_name?: string | null;
  email?: string | null;
  phone_number?: string | null;
  worker_location: string;
  avatar: string;
  primary_role: string;
  secondary_roles: string[];
  union_status: string;
  iatse_local: string;
  certifications: string[];
  worker_rate_min: number;
  worker_rate_max: number;
  worker_rate_type: string;
  equipment_proficiencies: string[];
  headshot_url: string;
  resume_url?: string | null;
}

export interface WorkerSearchResult {
  id: number;
  first_name: string;
  last_initial: string | null;
  location: string;
  avatar: string;
  rating: number;
  review_count: number;
  primary_role: string;
  secondary_roles: string[];
  union_status: string;
  iatse_local: string;
  certifications: string[];
  rate_min: number;
  rate_max: number;
  rate_type: string;
  equipment_proficiencies: string[];
  headshot_url: string;
  travel_willing: boolean;
  travel_radius_miles: number;
}

export interface EmployerStats {
  total_gigs: number;
  active_gigs: number;
  publicly_qualified_gigs: number;
  needs_attention_gigs: number;
  urgent_gigs: number;
  total_crew_needed: number;
  total_matches: number;
  booked_workers: number;
  avg_match_score: number;
}

export interface GigAnalytics {
  id: number;
  title: string;
  location: string;
  is_active: boolean;
  posting_tier: string | null;
  crew_size: number;
  created_at: string;
  start_date: string | null;
  total_matches: number;
  pending_matches: number;
  applied: number;
  offers_sent: number;
  booked: number;
  cancelled: number;
  avg_match_score: number;
  booking_rate: number;
  fill_rate: number;
}

export interface AnalyticsSummary {
  total_gigs: number;
  featured_gigs: number;
  total_matches: number;
  total_booked: number;
  total_offers: number;
  avg_match_score: number;
  booking_rate: number;
  total_spent: number;
  paid_postings: number;
}

export interface EmployerAnalytics {
  gigs: GigAnalytics[];
  summary: AnalyticsSummary;
}

export interface FavoriteWorker {
  id: number;
  worker_id: number;
  notes: string | null;
  favorited_at: string;
  first_name: string;
  last_initial: string | null;
  location: string | null;
  rating: number;
  review_count: number;
  avatar: string | null;
  primary_role: string | null;
  rate_min: number | null;
  rate_max: number | null;
  rate_type: string | null;
  union_status: string | null;
  certifications: string[] | string | null;
  equipment_proficiencies: string[] | string | null;
  headshot_url: string | null;
}

export interface InstantBookOffer {
  worker_id: number;
  name: string;
  match_score: number;
  sms_sent: boolean;
}

export interface InstantBookResult {
  message: string;
  offers: InstantBookOffer[];
}

export interface EmployerProfileData {
  id: number;
  user_id: number;
  company_name: string;
  company_website: string | null;
  company_description: string | null;
  company_size: string | null;
  industry: string | null;
  tax_id: string | null;
  stripe_customer_id: string | null;
  logo_url: string | null;
  address_line1: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  phone: string | null;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmployerSubscriptionData {
  id?: number;
  user_id?: number;
  stripe_subscription_id?: string | null;
  has_active_subscription?: boolean;
  plan: string | null;
  status: string;
  current_period_start?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean;
  postings_used_this_month?: number;
  postings_limit_per_month?: number;
  // Free post tracking (non-subscribers only)
  freePostsUsed?: number;
  freePostsRemaining?: number;
  freePostLimit?: number;
}

interface EmployerState {
  gigs: EmployerGig[];
  currentGig: EmployerGig | null;
  matchedWorkers: MatchedWorker[];
  workerSearchResults: WorkerSearchResult[];
  stats: EmployerStats | null;
  analytics: EmployerAnalytics | null;
  analyticsLoading: boolean;
  employerProfile: EmployerProfileData | null;
  profileLoading: boolean;
  profileError: string | null;
  subscription: EmployerSubscriptionData | null;
  subscriptionLoading: boolean;
  subscriptionError: string | null;
  subscriptionCheckoutUrl: string | null;
  instantBookResult: InstantBookResult | null;
  instantBookLoading: boolean;
  favorites: FavoriteWorker[];
  favoritesTotal: number;
  favoritesLoading: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: EmployerState = {
  gigs: [],
  currentGig: null,
  matchedWorkers: [],
  workerSearchResults: [],
  stats: null,
  analytics: null,
  analyticsLoading: false,
  employerProfile: null,
  profileLoading: false,
  profileError: null,
  subscription: null,
  subscriptionLoading: false,
  subscriptionError: null,
  subscriptionCheckoutUrl: null,
  instantBookResult: null,
  instantBookLoading: false,
  favorites: [],
  favoritesTotal: 0,
  favoritesLoading: false,
  loading: false,
  error: null,
};

export const fetchEmployerGigs = createAsyncThunk(
  'employer/fetchGigs',
  async (filters: { status?: string } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      const response = await api.get(`/employer/gigs?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to fetch gigs');
      return rejectWithValue('Failed to fetch gigs');
    }
  },
);

export const createEmployerGig = createAsyncThunk(
  'employer/createGig',
  async (gigData: Record<string, unknown>, { rejectWithValue }) => {
    try {
      const response = await api.post('/employer/gigs', gigData);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to post gig');
      return rejectWithValue('Failed to post gig');
    }
  },
);

export const createPaidGig = createAsyncThunk(
  'employer/createPaidGig',
  async (gigData: Record<string, unknown>, { rejectWithValue }) => {
    try {
      const response = await api.post('/employer/gigs/paid', gigData);
      return response.data as { checkoutUrl: string; sessionId: string; gigId: number };
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to create paid gig');
      return rejectWithValue('Failed to create paid gig');
    }
  },
);

export const updateEmployerGig = createAsyncThunk(
  'employer/updateGig',
  async ({ id, data }: { id: number; data: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/employer/gigs/${id}`, data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to update gig');
      return rejectWithValue('Failed to update gig');
    }
  },
);

export const deleteEmployerGig = createAsyncThunk(
  'employer/deleteGig',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.delete(`/employer/gigs/${id}`);
      return id;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to delete gig');
      return rejectWithValue('Failed to delete gig');
    }
  },
);

export const fetchGigMatches = createAsyncThunk(
  'employer/fetchGigMatches',
  async (gigId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/employer/gigs/${gigId}/matches`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to fetch matches');
      return rejectWithValue('Failed to fetch matches');
    }
  },
);

export const bookWorker = createAsyncThunk(
  'employer/bookWorker',
  async ({ gigId, workerId }: { gigId: number; workerId: number }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/employer/gigs/${gigId}/book/${workerId}`);
      return { gigId, workerId, ...response.data };
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to book worker');
      return rejectWithValue('Failed to book worker');
    }
  },
);

export const searchWorkers = createAsyncThunk(
  'employer/searchWorkers',
  async (filters: { role?: string; location?: string; union_status?: string; certification?: string } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await api.get(`/employer/workers/search?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to search workers');
      return rejectWithValue('Failed to search workers');
    }
  },
);

export const fetchEmployerStats = createAsyncThunk(
  'employer/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/employer/dashboard/stats');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to fetch stats');
      return rejectWithValue('Failed to fetch stats');
    }
  },
);

export const fetchEmployerAnalytics = createAsyncThunk(
  'employer/fetchAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/employer/dashboard/analytics');
      return response.data as EmployerAnalytics;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to fetch analytics');
      return rejectWithValue('Failed to fetch analytics');
    }
  },
);

export const fetchEmployerProfile = createAsyncThunk(
  'employer/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/employer-profile/profile');
      return response.data as EmployerProfileData;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) return rejectWithValue('not_found');
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch employer profile');
      }
      return rejectWithValue('Failed to fetch employer profile');
    }
  },
);

export const createEmployerProfile = createAsyncThunk(
  'employer/createProfile',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      const response = await api.post('/employer-profile/profile', data);
      return response.data as EmployerProfileData;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to create employer profile');
      return rejectWithValue('Failed to create employer profile');
    }
  },
);

export const updateEmployerProfile = createAsyncThunk(
  'employer/updateProfile',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      const response = await api.put('/employer-profile/profile', data);
      return response.data as EmployerProfileData;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to update employer profile');
      return rejectWithValue('Failed to update employer profile');
    }
  },
);

export const fetchEmployerSubscription = createAsyncThunk(
  'employer/fetchSubscription',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/employer-subscription/current');
      return response.data as EmployerSubscriptionData;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to fetch subscription');
      return rejectWithValue('Failed to fetch subscription');
    }
  },
);

export const createEmployerSubscription = createAsyncThunk(
  'employer/createSubscription',
  async (plan: string, { rejectWithValue }) => {
    try {
      const response = await api.post('/employer-subscription/create', { plan });
      return response.data as { checkoutUrl: string; sessionId: string };
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to create subscription');
      return rejectWithValue('Failed to create subscription');
    }
  },
);

export const openEmployerBillingPortal = createAsyncThunk(
  'employer/openBillingPortal',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/employer-subscription/billing-portal');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to open billing portal');
      return rejectWithValue('Failed to open billing portal');
    }
  },
);

export const cancelEmployerSubscription = createAsyncThunk(
  'employer/cancelSubscription',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/employer-subscription/cancel');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to cancel subscription');
      return rejectWithValue('Failed to cancel subscription');
    }
  },
);

export const instantBookGig = createAsyncThunk(
  'employer/instantBook',
  async ({ gigId, maxWorkers }: { gigId: number; maxWorkers: number }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/employer/gigs/${gigId}/instant-book`, { max_workers: maxWorkers });
      return response.data as InstantBookResult;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to send instant-book offers');
      return rejectWithValue('Failed to send instant-book offers');
    }
  },
);

export const fetchFavorites = createAsyncThunk(
  'employer/fetchFavorites',
  async ({ limit, offset }: { limit?: number; offset?: number } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', String(limit));
      if (offset) params.append('offset', String(offset));
      const response = await api.get(`/employer/favorites?${params.toString()}`);
      return response.data as { favorites: FavoriteWorker[]; total: number; limit: number; offset: number };
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to fetch favorites');
      return rejectWithValue('Failed to fetch favorites');
    }
  },
);

export const addFavorite = createAsyncThunk(
  'employer/addFavorite',
  async ({ workerId, notes }: { workerId: number; notes?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/employer/favorites/${workerId}`, { notes });
      return response.data as FavoriteWorker;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to add favorite');
      return rejectWithValue('Failed to add favorite');
    }
  },
);

export const removeFavorite = createAsyncThunk(
  'employer/removeFavorite',
  async (workerId: number, { rejectWithValue }) => {
    try {
      await api.delete(`/employer/favorites/${workerId}`);
      return workerId;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to remove favorite');
      return rejectWithValue('Failed to remove favorite');
    }
  },
);

export const updateFavoriteNotes = createAsyncThunk(
  'employer/updateFavoriteNotes',
  async ({ workerId, notes }: { workerId: number; notes: string }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/employer/favorites/${workerId}`, { notes });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.response?.data?.message || 'Failed to update notes');
      return rejectWithValue('Failed to update notes');
    }
  },
);

const employerSlice = createSlice({
  name: 'employer',
  initialState,
  reducers: {
    clearMatchedWorkers: (state) => {
      state.matchedWorkers = [];
    },
    clearSubscriptionCheckoutUrl: (state) => {
      state.subscriptionCheckoutUrl = null;
    },
    clearInstantBookResult: (state) => {
      state.instantBookResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch gigs
      .addCase(fetchEmployerGigs.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchEmployerGigs.fulfilled, (state, action) => { state.loading = false; state.gigs = action.payload; })
      .addCase(fetchEmployerGigs.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Create gig (free)
      .addCase(createEmployerGig.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createEmployerGig.fulfilled, (state, action) => {
        state.loading = false;
        state.gigs.unshift(action.payload);
        // Update free post count from server response
        if (action.payload.freePostsUsed !== undefined && state.subscription) {
          state.subscription.freePostsUsed = action.payload.freePostsUsed;
          state.subscription.freePostsRemaining = action.payload.freePostsRemaining;
        }
      })
      .addCase(createEmployerGig.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Create paid gig (Stripe checkout)
      .addCase(createPaidGig.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createPaidGig.fulfilled, (state) => { state.loading = false; })
      .addCase(createPaidGig.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Update gig
      .addCase(updateEmployerGig.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateEmployerGig.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.gigs.findIndex(g => g.id === action.payload.id);
        if (idx >= 0) state.gigs[idx] = action.payload;
      })
      .addCase(updateEmployerGig.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Delete gig
      .addCase(deleteEmployerGig.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(deleteEmployerGig.fulfilled, (state, action) => {
        state.loading = false;
        state.gigs = state.gigs.filter(g => g.id !== action.payload);
      })
      .addCase(deleteEmployerGig.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Fetch matches
      .addCase(fetchGigMatches.pending, (state) => { state.loading = true; })
      .addCase(fetchGigMatches.fulfilled, (state, action) => { state.loading = false; state.matchedWorkers = action.payload; })
      .addCase(fetchGigMatches.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Book worker
      .addCase(bookWorker.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(bookWorker.fulfilled, (state, action) => {
        state.loading = false;
        const worker = state.matchedWorkers.find(w => w.worker_id === action.payload.workerId);
        if (worker) worker.status = 'booked';
      })
      .addCase(bookWorker.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Search workers
      .addCase(searchWorkers.pending, (state) => { state.loading = true; })
      .addCase(searchWorkers.fulfilled, (state, action) => { state.loading = false; state.workerSearchResults = action.payload; })
      .addCase(searchWorkers.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Stats
      .addCase(fetchEmployerStats.pending, (state) => { state.loading = true; })
      .addCase(fetchEmployerStats.fulfilled, (state, action) => { state.loading = false; state.stats = action.payload; })
      .addCase(fetchEmployerStats.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Analytics
      .addCase(fetchEmployerAnalytics.pending, (state) => { state.analyticsLoading = true; })
      .addCase(fetchEmployerAnalytics.fulfilled, (state, action) => { state.analyticsLoading = false; state.analytics = action.payload; })
      .addCase(fetchEmployerAnalytics.rejected, (state) => { state.analyticsLoading = false; })
      // Employer profile
      .addCase(fetchEmployerProfile.pending, (state) => { state.profileLoading = true; state.profileError = null; })
      .addCase(fetchEmployerProfile.fulfilled, (state, action) => { state.profileLoading = false; state.employerProfile = action.payload; })
      .addCase(fetchEmployerProfile.rejected, (state, action) => { state.profileLoading = false; state.profileError = action.payload as string; })
      .addCase(createEmployerProfile.pending, (state) => { state.profileLoading = true; state.profileError = null; })
      .addCase(createEmployerProfile.fulfilled, (state, action) => { state.profileLoading = false; state.employerProfile = action.payload; })
      .addCase(createEmployerProfile.rejected, (state, action) => { state.profileLoading = false; state.profileError = action.payload as string; })
      .addCase(updateEmployerProfile.pending, (state) => { state.profileLoading = true; state.profileError = null; })
      .addCase(updateEmployerProfile.fulfilled, (state, action) => { state.profileLoading = false; state.employerProfile = action.payload; })
      .addCase(updateEmployerProfile.rejected, (state, action) => { state.profileLoading = false; state.profileError = action.payload as string; })
      // Employer subscription
      .addCase(fetchEmployerSubscription.pending, (state) => { state.subscriptionLoading = true; state.subscriptionError = null; })
      .addCase(fetchEmployerSubscription.fulfilled, (state, action) => { state.subscriptionLoading = false; state.subscription = action.payload; })
      .addCase(fetchEmployerSubscription.rejected, (state, action) => { state.subscriptionLoading = false; state.subscriptionError = action.payload as string; })
      .addCase(createEmployerSubscription.pending, (state) => { state.subscriptionLoading = true; state.subscriptionError = null; })
      .addCase(createEmployerSubscription.fulfilled, (state, action) => { state.subscriptionLoading = false; state.subscriptionCheckoutUrl = action.payload.checkoutUrl; })
      .addCase(createEmployerSubscription.rejected, (state, action) => { state.subscriptionLoading = false; state.subscriptionError = action.payload as string; })
      .addCase(cancelEmployerSubscription.pending, (state) => { state.subscriptionLoading = true; state.subscriptionError = null; })
      .addCase(cancelEmployerSubscription.fulfilled, (state, action) => { state.subscriptionLoading = false; if (action.payload.subscription) state.subscription = action.payload.subscription; })
      .addCase(cancelEmployerSubscription.rejected, (state, action) => { state.subscriptionLoading = false; state.subscriptionError = action.payload as string; })
      // Instant book
      .addCase(instantBookGig.pending, (state) => { state.instantBookLoading = true; state.instantBookResult = null; })
      .addCase(instantBookGig.fulfilled, (state, action) => { state.instantBookLoading = false; state.instantBookResult = action.payload; })
      .addCase(instantBookGig.rejected, (state, action) => { state.instantBookLoading = false; state.error = action.payload as string; })
      // Favorites (Talent Pool)
      .addCase(fetchFavorites.pending, (state) => { state.favoritesLoading = true; })
      .addCase(fetchFavorites.fulfilled, (state, action) => { state.favoritesLoading = false; state.favorites = action.payload.favorites; state.favoritesTotal = action.payload.total; })
      .addCase(fetchFavorites.rejected, (state) => { state.favoritesLoading = false; })
      .addCase(addFavorite.fulfilled, (state, action) => { state.favorites.unshift(action.payload); state.favoritesTotal += 1; })
      .addCase(removeFavorite.fulfilled, (state, action) => { state.favorites = state.favorites.filter(f => f.worker_id !== action.payload); state.favoritesTotal = Math.max(0, state.favoritesTotal - 1); })
      .addCase(updateFavoriteNotes.fulfilled, (state, action) => {
        const fav = state.favorites.find(f => f.worker_id === action.payload.worker_id);
        if (fav) fav.notes = action.payload.notes;
      });
  },
});

export const { clearMatchedWorkers, clearSubscriptionCheckoutUrl, clearInstantBookResult } = employerSlice.actions;
export default employerSlice.reducer;
